import React, { useState, useEffect } from 'react';
import { Heart, Plus, X, Search, Filter, Eye, Check, TrendingUp, AlertCircle, Globe } from 'lucide-react';
import { wishListAPI, clientAPI, watchAPI } from '../services/api';
import { useNavigate, useSearchParams } from 'react-router-dom';

const WishListPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('my-wishlists'); // 'my-wishlists' or 'network'
  const [wishLists, setWishLists] = useState([]);
  const [publicWishLists, setPublicWishLists] = useState([]);
  const [clients, setClients] = useState([]);
  const [watches, setWatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showMatchesModal, setShowMatchesModal] = useState(false);
  const [selectedWishList, setSelectedWishList] = useState(null);
  const [matches, setMatches] = useState([]);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const [formData, setFormData] = useState({
    client_id: null,
    brand: '',
    model: '',
    reference: '',
    year_min: '',
    year_max: '',
    condition: '',
    max_price: '',
    box_required: '',
    papers_required: '',
    notes: '',
    priority: 'Medium',
    is_public: false,
  });

  useEffect(() => {
    fetchWishLists();
    fetchPublicWishLists();
    fetchClients();
    fetchWatches();
  }, []);

  // Check for client_id in URL params and open modal with pre-selected client
  useEffect(() => {
    const clientIdParam = searchParams.get('client_id');
    if (clientIdParam && clients.length > 0) {
      const clientId = parseInt(clientIdParam);
      const client = clients.find(c => c.id === clientId);
      if (client) {
        setFormData(prev => ({ ...prev, client_id: clientId }));
        setShowModal(true);
        // Clear the URL parameter after using it
        navigate('/wishlists', { replace: true });
      }
    }
  }, [searchParams, clients]);

  const fetchWishLists = async () => {
    try {
      const response = await wishListAPI.getAll();
      setWishLists(response.data);
    } catch (error) {
      console.error('Error fetching wish lists:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPublicWishLists = async () => {
    try {
      const response = await wishListAPI.getPublic();
      setPublicWishLists(response.data);
    } catch (error) {
      console.error('Error fetching public wish lists:', error);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await clientAPI.getAll({ page: 1, per_page: 1000 });
      setClients(response.data.clients || []);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchWatches = async () => {
    try {
      const response = await watchAPI.getAll();
      setWatches(response.data);
    } catch (error) {
      console.error('Error fetching watches:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSubmit = {
        ...formData,
        client_id: formData.client_id === '' ? null : formData.client_id,
        year_min: formData.year_min === '' ? null : parseInt(formData.year_min),
        year_max: formData.year_max === '' ? null : parseInt(formData.year_max),
        max_price: formData.max_price === '' ? null : parseFloat(formData.max_price),
      };

      if (selectedWishList) {
        await wishListAPI.update(selectedWishList.id, dataToSubmit);
      } else {
        await wishListAPI.create(dataToSubmit);
      }
      setShowModal(false);
      resetForm();
      fetchWishLists();
      if (formData.is_public) {
        fetchPublicWishLists();
      }
    } catch (error) {
      console.error('Error saving wish list:', error);
      alert('Failed to save wish list');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this wish list?')) return;
    try {
      await wishListAPI.delete(id);
      fetchWishLists();
      fetchPublicWishLists();
    } catch (error) {
      console.error('Error deleting wish list:', error);
    }
  };

  const handleEdit = (wishList) => {
    setSelectedWishList(wishList);
    setFormData({
      client_id: wishList.client_id || null,
      brand: wishList.brand,
      model: wishList.model || '',
      reference: wishList.reference || '',
      year_min: wishList.year_min || '',
      year_max: wishList.year_max || '',
      condition: wishList.condition || '',
      max_price: wishList.max_price || '',
      box_required: wishList.box_required || '',
      papers_required: wishList.papers_required || '',
      notes: wishList.notes || '',
      priority: wishList.priority || 'Medium',
      is_public: wishList.is_public || false,
    });
    setShowModal(true);
  };

  const handleViewMatches = async (wishList) => {
    try {
      const response = await wishListAPI.getMatches(wishList.id);
      setMatches(response.data.matches);
      setSelectedWishList(wishList);
      setShowMatchesModal(true);
    } catch (error) {
      console.error('Error fetching matches:', error);
    }
  };

  const handleMarkFulfilled = async (wishListId, watchId) => {
    try {
      await wishListAPI.markFulfilled(wishListId, watchId);
      setShowMatchesModal(false);
      fetchWishLists();
    } catch (error) {
      console.error('Error marking as fulfilled:', error);
    }
  };

  const handleMessageDealer = (wishList) => {
    navigate(`/messages?userId=${wishList.dealer_id}&userName=${encodeURIComponent(wishList.dealer_name)}&businessName=${encodeURIComponent(wishList.dealer_business_name || '')}`);
  };

  const resetForm = () => {
    setFormData({
      client_id: null,
      brand: '',
      model: '',
      reference: '',
      year_min: '',
      year_max: '',
      condition: '',
      max_price: '',
      box_required: '',
      papers_required: '',
      notes: '',
      priority: 'Medium',
      is_public: false,
    });
    setSelectedWishList(null);
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'High': return 'bg-red-100 text-red-800 border-red-200';
      case 'Medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Low': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Fulfilled': return 'bg-green-100 text-green-800 border-green-200';
      case 'Cancelled': return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const filteredWishLists = Array.isArray(wishLists) ? wishLists.filter((wishList) => {
    if (filterStatus && wishList.status !== filterStatus) return false;
    if (filterPriority && wishList.priority !== filterPriority) return false;
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        wishList.brand.toLowerCase().includes(search) ||
        (wishList.model && wishList.model.toLowerCase().includes(search)) ||
        (wishList.client_name && wishList.client_name.toLowerCase().includes(search))
      );
    }
    return true;
  }) : [];

  const filteredPublicWishLists = Array.isArray(publicWishLists) ? publicWishLists.filter((wishList) => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        wishList.brand.toLowerCase().includes(search) ||
        (wishList.model && wishList.model.toLowerCase().includes(search)) ||
        (wishList.dealer_name && wishList.dealer_name.toLowerCase().includes(search))
      );
    }
    return true;
  }) : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-slate-900 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Heart className="w-8 h-8 text-slate-900" />
            Wish Lists & Sourcing
          </h1>
          <p className="text-gray-600 mt-1">Track client requests and discover what the network needs</p>
        </div>
        {activeTab === 'my-wishlists' && (
          <button
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            className="px-6 py-3 bg-gradient-to-r from-slate-900 to-slate-700 text-white rounded-xl font-semibold shadow-lg hover:from-slate-950 hover:to-slate-800 transition-all flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Add Wish List
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-3xl shadow-xl border-2 border-gray-100 p-2">
        <div className="flex gap-2">
          <button
            onClick={() => setActiveTab('my-wishlists')}
            className={`flex-1 px-6 py-3 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 ${
              activeTab === 'my-wishlists'
                ? 'bg-gradient-to-r from-slate-900 to-slate-700 text-white shadow-lg'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <Heart className="w-5 h-5" />
            My Wish Lists ({wishLists.length})
          </button>
          <button
            onClick={() => setActiveTab('network')}
            className={`flex-1 px-6 py-3 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 ${
              activeTab === 'network'
                ? 'bg-gradient-to-r from-slate-900 to-slate-700 text-white shadow-lg'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <Globe className="w-5 h-5" />
            Network Requests ({publicWishLists.length})
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-3xl shadow-xl border-2 border-gray-100 p-6">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2 flex-1 min-w-[200px]">
            <Search className="w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder={activeTab === 'my-wishlists' ? "Search by brand, model, or client..." : "Search by brand, model, or dealer..."}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
            />
          </div>

          {activeTab === 'my-wishlists' && (
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-gray-400" />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
              >
                <option value="">All Statuses</option>
                <option value="Active">Active</option>
                <option value="Fulfilled">Fulfilled</option>
                <option value="Cancelled">Cancelled</option>
              </select>

              <select
                value={filterPriority}
                onChange={(e) => setFilterPriority(e.target.value)}
                className="px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
              >
                <option value="">All Priorities</option>
                <option value="High">High Priority</option>
                <option value="Medium">Medium Priority</option>
                <option value="Low">Low Priority</option>
              </select>
            </div>
          )}
        </div>
      </div>

      {/* My Wish Lists Tab */}
      {activeTab === 'my-wishlists' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredWishLists.map((wishList) => (
            <div
              key={wishList.id}
              className="bg-white rounded-3xl shadow-xl border-2 border-gray-100 p-6 hover:shadow-2xl transition-all"
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900">{wishList.brand}</h3>
                  {wishList.model && (
                    <p className="text-gray-600 mt-1">{wishList.model}</p>
                  )}
                  {wishList.client_name && (
                    <p className="text-sm text-gray-500 mt-1">For: {wishList.client_name}</p>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <span className={`px-3 py-1 rounded-full text-xs font-bold border-2 ${getPriorityColor(wishList.priority)}`}>
                    {wishList.priority}
                  </span>
                  {wishList.is_public && (
                    <span className="px-3 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-800 border-2 border-blue-200 flex items-center gap-1">
                      <Globe className="w-3 h-3" />
                      Public
                    </span>
                  )}
                </div>
              </div>

              {/* Details */}
              <div className="space-y-2 text-sm text-gray-600 mb-4">
                {wishList.reference && (
                  <p><span className="font-semibold">Reference:</span> {wishList.reference}</p>
                )}
                {(wishList.year_min || wishList.year_max) && (
                  <p>
                    <span className="font-semibold">Year:</span>{' '}
                    {wishList.year_min && wishList.year_max
                      ? `${wishList.year_min} - ${wishList.year_max}`
                      : wishList.year_min
                      ? `${wishList.year_min}+`
                      : `Up to ${wishList.year_max}`}
                  </p>
                )}
                {wishList.condition && (
                  <p><span className="font-semibold">Condition:</span> {wishList.condition}</p>
                )}
                {wishList.max_price && (
                  <p><span className="font-semibold">Max Price:</span> ${(Number(wishList.max_price) || 0).toLocaleString()}</p>
                )}
                {wishList.notes && (
                  <p className="text-xs mt-2 p-2 bg-amber-50 rounded-lg border border-amber-200"><span className="font-semibold text-amber-800">Private Notes:</span> <span className="text-gray-700">{wishList.notes}</span></p>
                )}
              </div>

              {/* Status and Matches */}
              <div className="flex items-center justify-between mb-4">
                <span className={`px-3 py-1 rounded-full text-xs font-bold border-2 ${getStatusColor(wishList.status)}`}>
                  {wishList.status}
                </span>
                {wishList.match_count > 0 && (
                  <div className="flex items-center gap-1 text-green-600 font-bold">
                    <TrendingUp className="w-4 h-4" />
                    {wishList.match_count} {wishList.match_count === 1 ? 'match' : 'matches'}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                {wishList.match_count > 0 && wishList.status === 'Active' && (
                  <button
                    onClick={() => handleViewMatches(wishList)}
                    className="flex-1 px-4 py-2 bg-gradient-to-r from-green-600 to-green-500 text-white rounded-xl font-semibold hover:from-green-700 hover:to-green-600 transition-all flex items-center justify-center gap-2"
                  >
                    <Eye className="w-4 h-4" />
                    Matches
                  </button>
                )}
                <button
                  onClick={() => handleEdit(wishList)}
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-slate-900 to-slate-700 text-white rounded-xl font-semibold hover:from-slate-950 hover:to-slate-800 transition-all"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(wishList.id)}
                  className="px-4 py-2 bg-red-600 text-white rounded-xl font-semibold hover:bg-red-700 transition-all"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}

          {filteredWishLists.length === 0 && (
            <div className="col-span-full bg-white rounded-3xl shadow-xl border-2 border-gray-100 p-12 text-center">
              <Heart className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">No Wish Lists Yet</h3>
              <p className="text-gray-600 mb-6">
                Create wish lists to track client sourcing requests and get notified when matching watches become available.
              </p>
              <button
                onClick={() => {
                  resetForm();
                  setShowModal(true);
                }}
                className="px-6 py-3 bg-gradient-to-r from-slate-900 to-slate-700 text-white rounded-xl font-semibold shadow-lg hover:from-slate-950 hover:to-slate-800 transition-all inline-flex items-center gap-2"
              >
                <Plus className="w-5 h-5" />
                Create Your First Wish List
              </button>
            </div>
          )}
        </div>
      )}

      {/* Network Requests Tab */}
      {activeTab === 'network' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPublicWishLists.map((wishList) => (
            <div
              key={wishList.id}
              className="bg-white rounded-3xl shadow-xl border-2 border-gray-100 p-6 hover:shadow-2xl transition-all"
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900">{wishList.brand}</h3>
                  {wishList.model && (
                    <p className="text-gray-600 mt-1">{wishList.model}</p>
                  )}
                  <div className="mt-2 flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center text-white font-bold text-sm">
                      {wishList.dealer_name?.charAt(0)}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{wishList.dealer_name}</p>
                      {wishList.dealer_business_name && (
                        <p className="text-xs text-gray-500">{wishList.dealer_business_name}</p>
                      )}
                    </div>
                  </div>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-bold border-2 ${getPriorityColor(wishList.priority)}`}>
                  {wishList.priority}
                </span>
              </div>

              {/* Details */}
              <div className="space-y-2 text-sm text-gray-600 mb-4">
                {wishList.reference && (
                  <p><span className="font-semibold">Reference:</span> {wishList.reference}</p>
                )}
                {(wishList.year_min || wishList.year_max) && (
                  <p>
                    <span className="font-semibold">Year:</span>{' '}
                    {wishList.year_min && wishList.year_max
                      ? `${wishList.year_min} - ${wishList.year_max}`
                      : wishList.year_min
                      ? `${wishList.year_min}+`
                      : `Up to ${wishList.year_max}`}
                  </p>
                )}
                {wishList.condition && (
                  <p><span className="font-semibold">Condition:</span> {wishList.condition}</p>
                )}
                {wishList.max_price && (
                  <p><span className="font-semibold">Max Price:</span> ${(Number(wishList.max_price) || 0).toLocaleString()}</p>
                )}
                {/* Notes are private - not shown in public network view */}
              </div>

              {/* Action */}
              <button
                onClick={() => handleMessageDealer(wishList)}
                className="w-full px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-blue-600 transition-all flex items-center justify-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Offer Match
              </button>
            </div>
          ))}

          {filteredPublicWishLists.length === 0 && (
            <div className="col-span-full bg-white rounded-3xl shadow-xl border-2 border-gray-100 p-12 text-center">
              <Globe className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">No Network Requests</h3>
              <p className="text-gray-600">
                No other dealers have shared public wish lists yet. When they do, you'll see them here and can offer matches from your inventory.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900">
                  {selectedWishList ? 'Edit Wish List' : 'Create Wish List'}
                </h2>
                <button
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="p-2 hover:bg-gray-100 rounded-xl transition-all"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Client */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Client (Optional)
                </label>
                <select
                  value={formData.client_id || ''}
                  onChange={(e) => setFormData({ ...formData, client_id: e.target.value === '' ? null : parseInt(e.target.value) })}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
                >
                  <option value="">No client (general sourcing)</option>
                  {Array.isArray(clients) && clients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Brand and Model */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Brand *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.brand}
                    onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Model
                  </label>
                  <input
                    type="text"
                    value={formData.model}
                    onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
                  />
                </div>
              </div>

              {/* Reference */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Reference Number
                </label>
                <input
                  type="text"
                  value={formData.reference}
                  onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
                />
              </div>

              {/* Year Range */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Year Min
                  </label>
                  <input
                    type="number"
                    value={formData.year_min}
                    onChange={(e) => setFormData({ ...formData, year_min: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Year Max
                  </label>
                  <input
                    type="number"
                    value={formData.year_max}
                    onChange={(e) => setFormData({ ...formData, year_max: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
                  />
                </div>
              </div>

              {/* Condition and Max Price */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Condition
                  </label>
                  <select
                    value={formData.condition}
                    onChange={(e) => setFormData({ ...formData, condition: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
                  >
                    <option value="">Any</option>
                    <option value="New">New</option>
                    <option value="Unworn">Unworn</option>
                    <option value="Like New">Like New</option>
                    <option value="Excellent">Excellent</option>
                    <option value="Very Good">Very Good</option>
                    <option value="Good">Good</option>
                    <option value="Fair">Fair</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Max Price ($)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.max_price}
                    onChange={(e) => setFormData({ ...formData, max_price: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
                  />
                </div>
              </div>

              {/* Box and Papers */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Box Required
                  </label>
                  <select
                    value={formData.box_required}
                    onChange={(e) => setFormData({ ...formData, box_required: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
                  >
                    <option value="">No Preference</option>
                    <option value="Required">Required</option>
                    <option value="Preferred">Preferred</option>
                    <option value="Not Required">Not Required</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Papers Required
                  </label>
                  <select
                    value={formData.papers_required}
                    onChange={(e) => setFormData({ ...formData, papers_required: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
                  >
                    <option value="">No Preference</option>
                    <option value="Required">Required</option>
                    <option value="Preferred">Preferred</option>
                    <option value="Not Required">Not Required</option>
                  </select>
                </div>
              </div>

              {/* Priority */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Priority
                </label>
                <select
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
                >
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                </select>
              </div>

              {/* Public Toggle */}
              <div className="flex items-center gap-3 p-4 bg-blue-50 border-2 border-blue-200 rounded-xl">
                <input
                  type="checkbox"
                  id="is_public"
                  checked={formData.is_public}
                  onChange={(e) => setFormData({ ...formData, is_public: e.target.checked })}
                  className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="is_public" className="flex-1 cursor-pointer">
                  <div className="flex items-center gap-2">
                    <Globe className="w-5 h-5 text-blue-600" />
                    <span className="font-semibold text-gray-900">Share with dealer network</span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    Other dealers can see this request and offer matches from their inventory
                  </p>
                </label>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows="3"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none"
                />
              </div>

              {/* Buttons */}
              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-slate-900 to-slate-700 text-white rounded-xl font-semibold shadow-lg hover:from-slate-950 hover:to-slate-800 transition-all"
                >
                  {selectedWishList ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Matches Modal */}
      {showMatchesModal && selectedWishList && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">
                    Matches for {selectedWishList.brand} {selectedWishList.model}
                  </h2>
                  <p className="text-gray-600 mt-1">{matches.length} matching watches found</p>
                </div>
                <button
                  onClick={() => setShowMatchesModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-xl transition-all"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6">
              {matches.length === 0 ? (
                <div className="text-center py-12">
                  <AlertCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-600">No matching watches found</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {Array.isArray(matches) && matches.map((watch) => (
                    <div
                      key={watch.id}
                      className="bg-gray-50 rounded-2xl p-6 border-2 border-gray-200 hover:border-slate-300 transition-all"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-gray-900">
                            {watch.brand} {watch.model}
                          </h3>
                          <div className="grid grid-cols-2 gap-4 mt-3 text-sm">
                            {watch.reference && (
                              <p className="text-gray-600">
                                <span className="font-semibold">Ref:</span> {watch.reference}
                              </p>
                            )}
                            {watch.year && (
                              <p className="text-gray-600">
                                <span className="font-semibold">Year:</span> {watch.year}
                              </p>
                            )}
                            {watch.condition && (
                              <p className="text-gray-600">
                                <span className="font-semibold">Condition:</span> {watch.condition}
                              </p>
                            )}
                            <p className="text-gray-600">
                              <span className="font-semibold">Price:</span> ${(Number(watch.asking_price) || 0).toLocaleString()}
                            </p>
                            {watch.box && (
                              <p className="text-gray-600">
                                <span className="font-semibold">Box:</span> {watch.box}
                              </p>
                            )}
                            {watch.papers && (
                              <p className="text-gray-600">
                                <span className="font-semibold">Papers:</span> {watch.papers}
                              </p>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => handleMarkFulfilled(selectedWishList.id, watch.id)}
                          className="ml-4 px-6 py-3 bg-gradient-to-r from-green-600 to-green-500 text-white rounded-xl font-semibold hover:from-green-700 hover:to-green-600 transition-all flex items-center gap-2"
                        >
                          <Check className="w-5 h-5" />
                          Mark Fulfilled
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WishListPage;
